<!-- @catalog Card-style dialog for confirm / prompt flows. Lighter than CModal; see CDialogInput, CDialogEvent for ready-made variants. -->
<template>
  <q-dialog ref="dialogRef" :persistent @hide="onDialogHide">
    <q-card
      class="q-dialog-plugin tw-flex tw-max-h-[90vh] tw-w-full tw-flex-col !tw-rounded-2xl !tw-shadow-xl"
    >
      <div class="tw-flex tw-items-center tw-gap-4 tw-border-b tw-px-6 tw-py-5">
        <q-icon
          v-if="dialogType.icon"
          :class="dialogType.class"
          :name="dialogType.icon"
        />

        <h3
          class="tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-xl tw-font-semibold"
        >
          {{ title }}
        </h3>

        <c-button-icon
          flat
          color="primary"
          icon="fas fa-times"
          @click="onDialogCancel"
        />
      </div>

      <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <div
          class="tw-flex-shrink tw-space-y-6 tw-overflow-y-auto tw-p-6 tw-pb-3"
        >
          <div v-if="message">
            <p class="tw-m-0 tw-text-base">{{ message }}</p>
          </div>

          <div>
            <slot />
          </div>
        </div>

        <div
          class="tw-flex tw-flex-shrink-0 tw-justify-end tw-gap-1 tw-p-6 tw-pt-3"
        >
          <c-button
            v-if="cancel"
            color="transparent"
            text-color="gray"
            label="Annuleren"
            @click="onDialogCancel"
          />

          <c-button
            id="primary-button"
            :disable="disableSubmit"
            :color="dialogType.color"
            :label="primaryLabel"
            @click="onOkClick"
          />
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { useConfetti } from "composables/useConfetti";
import { useDialogPluginComponent } from "quasar";
import { computed } from "vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
    default: () => "",
  },
  message: {
    type: [String, null],
    default: () => null,
  },
  cancel: {
    type: Boolean,
    default: () => true,
  },
  persistent: {
    type: Boolean,
    default: () => false,
  },
  primaryLabel: {
    type: String,
    default: () => "",
  },
  confetti: {
    type: Boolean,
    default: false,
  },
  validate: {
    type: Function,
    default: () => () => true,
  },
  type: {
    type: String,
  },
  returnData: {
    type: [String, Object, Array, null],
    default: "",
  },
  disableSubmit: {
    type: Boolean,
    default: () => false,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const dialogType = computed(() => {
  switch (props.type) {
    case "warning":
      return {
        class: "tw-text-warning",
        icon: "fas fa-exclamation-triangle",
        color: "warning",
      };
    case "negative":
      return {
        class: "tw-text-negative",
        icon: "fas fa-exclamation-circle",
        color: "negative",
      };
    case "success":
      return {
        class: "tw-text-success",
        icon: "fas fa-check-circle",
        color: "positive",
      };
    case "info":
      return {
        class: "tw-text-info",
        icon: "fas fa-info-circle",
        color: "green",
      };
    default:
      return {
        color: "green",
      };
  }
});

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

defineExpose({ dialogRef });

const onOkClick = async () => {
  const { validate = () => true } = props || {};

  if (await validate()) {
    const { confetti } = props;

    if (confetti) {
      useConfetti("#primary-button");
    }

    onDialogOK(props.returnData);
  }
};
</script>
